<template>
  <section class="GroupManagement">
    <Card class="GroupManagement__card">
      <section class="GroupManagement__mainSection">
        <div class="GroupManagement__inner">
          <SearchForm
            id="search"
            class="GroupManagement__searchForm"
            name="search"
            :placeholder="messages.common.searchPlaceholder"
            :value="searchWord"
            @inputWord="searchWord = $event"
          />
          <ul class="GroupManagement__list">
            <li class="GroupManagement__listItem">
              <Button
                class="GroupManagement__button"
                :label="messages.group.addMember"
                dynamics="primary"
                tag="button"
                :isDisabled="isListLoading || isGroupAssignDialogOpen"
                @click="openGroupAssignDialog()"
              />
            </li>
          </ul>
        </div>
        <SelectedSettings
          v-if="checkedItemsCount"
          v-model:isTableChecked="isThChecked"
          v-model:isAllSelected="isAllSelected"
          class="GroupManagement__selectedSettings"
          :selectedNum="checkedItemsCount"
          :allNum="totalCheckboxCount"
          :itemsPerPage="MAX_DISPLAY_NUM"
          :labelName="messages.group.deleteMember"
          :onDeleteButtonClick="onClickDeleteModalOpen"
          @update:isAllSelected="toggleAllChecked"
        />
        <UserList
          v-model:isThChecked="isThChecked"
          :displayUsers="displayUsers"
          :isListLoading="!isGroupAssignDialogOpen && isListLoading"
          :headList="headList"
          :noDataText="noDataText"
          :noDataImgUrl="noDataImageUrl"
          @onCheckHeader="onCheckHeader"
          @onCheckItem="onCheckItem"
        />
        <div
          v-if="isDefaultGroup && (isListLoading || displayUsers.length)"
          class="GroupManagement__information"
        >
          {{ messages.common.referenceMark }}
          <template v-if="defaultGroupName">
            <span class="GroupManagement__highlight">{{
              defaultGroupName
            }}</span>
          </template>
          <template v-else>
            <span class="GroupManagement__infoSkeleton shimmer"></span>
          </template>
          {{ messages.group.groupManagement.info.defaultGroup }}
        </div>
        <PaginationBar
          class="GroupManagement__pagination"
          :currentPage="currentPage"
          :maxDisplayPages="maxDisplayPages"
          :itemsPerPage="MAX_DISPLAY_NUM"
          :total="totalCount"
          @onClick:pageNumber="movePage"
        />
      </section>

      <Modal
        v-if="isOpenModal"
        ref="modal"
        class="GroupManagement__modal"
        :heading="heading"
        :size="isExtraSmallPixel ? 'extraSmall' : 'small'"
        @closeModal="onClickCancelButton"
      >
        <template v-if="!isError">
          <!-- safe usage of v-html: content is fully static and developer-controlled -->
          <p
            class="GroupManagement__modalText"
            v-html="messages.group.groupManagement.confirm.removeGroup"
          ></p>

          <ul class="GroupManagement__modalButton">
            <li class="GroupManagement__modalItem">
              <Button
                :isDisabled="isUpdateGroupLoading"
                :label="messages.common.cancel"
                dynamics="secondary"
                tag="button"
                @click="onClickCancelButton"
              />
            </li>
            <li class="GroupManagement__modalItem">
              <Button
                :isDisabled="isUpdateGroupLoading"
                :label="messages.common.release"
                dynamics="danger"
                tag="button"
                @click="onClickRemove"
              />
            </li>
          </ul>
        </template>
        <template v-else>
          <Icon class="GroupManagement__icon" iconName="Exception"></Icon>
          <!-- safe usage of v-html: content is fully static and developer-controlled -->
          <p class="GroupManagement__modalText" v-html="errorText"></p>
          <div class="GroupManagement__modalButton">
            <Button
              :label="messages.common.close"
              dynamics="secondary"
              tag="button"
              @click="onClickDeleteClose"
            />
          </div>
        </template>
      </Modal>
      <GroupAssignDialog
        v-if="isGroupAssignDialogOpen"
        :displayUsers="groupNotAssignedUsers"
        :isListLoading="isListLoading"
        @onClickModalClose="onGroupDialogClose"
        @onClickAssign="onClickGroupAssign"
      >
      </GroupAssignDialog>
    </Card>
  </section>
</template>

<script lang="ts">
export default {
  name: 'GroupManagement',
};
</script>

<script lang="ts" setup>
import Button from '@spectee/components/Atoms/Button/index.vue';
import Modal from '@spectee/components/Organisms/Modal/index.vue';
import Card from '@spectee/components/Atoms/Card/index.vue';
import Icon from '@spectee/components/Atoms/IconBase/index.vue';
import SearchForm from '@spectee/components/Atoms/SearchForm/index.vue';
import PaginationBar from '@spectee/components/Molecules/PaginationBar/index.vue';
import SelectedSettings from '@spectee/components/Molecules/SelectedSettings/index.vue';
import UserList from '@spectee/components/Organisms/UserList/index.vue';
import { Ref, computed, ref, watch, watchEffect } from 'vue';
import { useOverlayLoading } from '@spectee/composables/useOverlayLoading';
import GroupAssignDialog from '@spectee/components/Organisms/GroupAssignDialog/index.vue';
import { useListAllUsers } from '@spectee/graphql/queries/listAllUsers';
import {
  User,
  Authority,
  Message,
  Users,
  Action,
  AddOrRemoveGroupInput,
  Group,
  LicenseData,
} from '@spectee/graphql/api';
import { useRoute, useRouter } from 'vue-router';
import { useFeedback } from '@spectee/composables/useFeedback';
import { useAddRemoveMembersFromGroup } from '@spectee/graphql/mutations/addRemoveMembersFromGroup';
import { userStatus } from '@spectee/ts/userStatus';
import noDataImage from '@spectee/assets/images/img_notFound.png';
import noSearchDataImage from '@spectee/assets/images/img_search_noItem.svg';
import { HeadItem } from '@spectee/types/atoms';
import { useWindowWidth } from '@spectee/composables/useWindowWidth';
import messages from '../../../../messages.json';
import { isNotNullish } from '@spectee/ts/isNotNullish';

const route = useRoute();
const router = useRouter();

interface Props {
  groupId: string;
  isDefaultGroup: boolean;
  users?: Users;
}

const props = withDefaults(defineProps<Props>(), {
  users: Users.both,
});

const { isExtraSmallPixel } = useWindowWidth();

const emit = defineEmits<{
  (e: 'onUpdateGroup'): void;
}>();

const defaultGroupName = ref('');

const usersNeeded = ref(props.users);
const filterOption = ref('match');
const heading = ref('');
const errorText = ref('');

const groupRemoveError = messages.group.groupManagement.fail.removeGroup;
const groupAssignError = messages.group.groupManagement.fail.assignGroup;

const MAX_DISPLAY_NUM = 50;
const maxDisplayPages = 5;
const currentPage = ref(1);
const isThChecked = ref(false);
const isAllSelected = ref(false);
const checkedItemsCount = ref(0);
const totalCount = ref(0);
const totalCheckboxCount = ref(0);

const queryParams = route.query;

const headList: HeadItem[] = [
  { key: 'name', label: messages.common.headList.name },
  { key: 'displayName', label: messages.common.headList.displayName },
  { key: 'email', label: messages.common.headList.email },
  { key: 'groupName', label: messages.common.headList.groupName },
  { key: 'status', label: messages.common.headList.status },
  { key: 'authority', label: messages.common.headList.authority },
];

const isGroupAssignDialogOpen = ref(false);

const openGroupAssignDialog = async () => {
  isGroupAssignDialogOpen.value = true;
  if (filterOption.value === 'notMatch') {
    await listRefetch();
  } else {
    usersNeeded.value = Users.active; //need to check
    filterOption.value = 'notMatch';
  }
};

const onGroupDialogClose = () => {
  isGroupAssignDialogOpen.value = false;
};

//// リストの取得・表示関係の処理

// ユーザー・招待の一覧
const groupAssignedUsers: Ref<UserItem[]> = ref([]);
const groupNotAssignedUsers: Ref<UserItem[]> = ref([]);

// アクティブ・招待中ユーザーで共通の型
type UserStatus = (typeof userStatus)[keyof typeof userStatus];

type UserItem = {
  user: {
    email: string;
    authority: Authority;
    auth0UserId: string;
    name: string;
    displayName: string;
    groups: Group[];
    licenses: LicenseData[];
  };
  status: UserStatus;
  isChecked: boolean;
  isHideCheckBox?: boolean;
};

watch(
  () => route.query,
  () => {
    if (Object.keys(route.query).length === 0) {
      router.push({
        path: route.path,
        query: { ...queryParams }, // Preserve existing query params
      });
    }
  }
);

const variables = computed(() => {
  return {
    filter: {
      users: usersNeeded.value,
      groupId: {
        [filterOption.value]: props.groupId,
      },
    },
  };
});

const {
  listAllUsersRes: listUsersRes,
  loading: isListLoading,
  refetch: listRefetch,
} = useListAllUsers(variables, {});

// ユーザー・招待の一覧を取得、UserItem型に丸めて結合し、groupAssignedUsersに格納
// groupAssignedUsersの値は、取得時のほか、フィルタリング実行時・チェックボックス操作時にも変更される為、computedで定義せず、定義済みのrefに都度代入する
type UserWithStatus = User & { status: UserStatus };

watchEffect(() => {
  if (!listUsersRes.value || isListLoading.value) return;

  // Detect and store the default group name (same logic as GroupList)
  const defaultGroupObj = (listUsersRes.value as any[])
    .flatMap((user: User) => user.groups || [])
    .find((group: Group) => group.isDefault);
  if (defaultGroupObj && defaultGroupObj.name) {
    defaultGroupName.value = defaultGroupObj.name;
  }

  const mappedUsers = (listUsersRes.value as UserWithStatus[]).map(
    (user: UserWithStatus) => ({
      user: {
        email: user.email,
        authority: user.authority,
        auth0UserId: user.auth0UserId ?? '',
        name: user.name ?? '',
        displayName: user.displayName ?? '',
        groupName: user.groups?.map(group => group.name).join(',') || '',
        groups: user.groups,
        licenses: (user.licenseData ?? []).filter(isNotNullish),
      },
      status: user.status,
      isChecked: false,
      ...(filterOption.value === 'match' && {
        isHideCheckBox: props.isDefaultGroup && user.groups.length === 1,
      }),
    })
  );

  if (filterOption.value === 'match') {
    groupAssignedUsers.value = mappedUsers;
  } else {
    groupNotAssignedUsers.value = mappedUsers;
  }
});

const { pushFeedback } = useFeedback();

//// フィルタリング関係の処理
const searchWord = ref('');

// フィルター後のユーザー一覧
const filteredUser = computed(() => {
  if (searchWord.value) {
    const regex = new RegExp(searchWord.value, 'i');
    return groupAssignedUsers.value.filter(
      userItem =>
        regex.test(userItem.user.name) ||
        regex.test(userItem.user.email) ||
        regex.test(userItem.user.authority?.displayName)
    );
  }
  return groupAssignedUsers.value;
});

const noDataText = computed(() => {
  if (
    filteredUser.value.length === 0 &&
    groupAssignedUsers.value.length !== 0
  ) {
    return messages.user.info.noMatchingUsers;
  } else {
    return messages.user.info.noUser;
  }
});

const noDataImageUrl = computed(() => {
  if (
    filteredUser.value.length === 0 &&
    groupAssignedUsers.value.length !== 0
  ) {
    return noSearchDataImage;
  } else {
    return noDataImage;
  }
});

// フィルタリングが動いた際、フィルター外のユーザーのチェックを外す
watchEffect(() => {
  groupAssignedUsers.value.forEach(user => {
    if (!filteredUser.value.includes(user)) {
      user.isChecked = false;
    }
  });
});

//// チェックボックス関係の処理

// チェックボックスの選択
const onCheckItem = (event: Event, checkedUser: UserItem) => {
  const { target } = event;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }
  // チェックされたユーザーの判定は、本来メールアドレスの一致のみで判定できるはず
  // 現状は、アクティブユーザーと同じメールアドレスの招待が作れてしまっているため、アクティブユーザーならauth0UserId,招待ならメールアドレスで判定している
  groupAssignedUsers.value.forEach(user => {
    if (
      (checkedUser.status === user.status &&
        checkedUser.status === userStatus.active &&
        user.user.auth0UserId === checkedUser.user.auth0UserId) ||
      (checkedUser.status === user.status &&
        checkedUser.status !== userStatus.active &&
        user.user.email === checkedUser.user.email)
    ) {
      user.isChecked = target.checked;
    }
  });

  checkedItemsCount.value = checkedFilterItemsLength();
  const checkedDisplayUsersCount = checkedDisplayItemsLength();
  if (checkedDisplayUsersCount === displayUsers.value.length) {
    isThChecked.value = true;
  } else {
    isThChecked.value = false;
  }
};

const checkedFilterItemsLength = () => {
  return filteredUser.value.filter(user => user.isChecked).length;
};

const checkedDisplayItemsLength = () => {
  return displayUsers.value.filter(user => user.isChecked).length;
};

const checkboxDisplayItemsLength = () => {
  return displayUsers.value.filter(user => !user.isHideCheckBox).length;
};

watchEffect(() => {
  checkedItemsCount.value = checkedFilterItemsLength();
  totalCount.value = filteredUser.value.length;
  totalCheckboxCount.value =
    filteredUser.value?.filter(user => !user.isHideCheckBox).length || 0;
});

const onCheckHeader = (event: Event) => {
  const { target } = event;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }
  groupAssignedUsers.value.forEach(user => {
    if (displayUsers.value.includes(user) && !user.isHideCheckBox) {
      user.isChecked = target.checked;
    }
  });
  checkedItemsCount.value = checkedFilterItemsLength();
  const checkedDisplayUsersCount = checkedDisplayItemsLength();
  const checkboxDisplayItemsCount = checkboxDisplayItemsLength();
  isThChecked.value =
    checkedDisplayUsersCount === checkboxDisplayItemsCount ? true : false;
};

const toggleAllChecked = (isAllChecked: boolean) => {
  groupAssignedUsers.value.forEach(user => {
    if (filteredUser.value.includes(user) && !user.isHideCheckBox) {
      user.isChecked = isAllChecked;
    }
  });
  isThChecked.value = isAllChecked;
  isAllSelected.value = isAllChecked;
};

const { mutate: addRemoveMembersFromGroup, loading: isUpdateGroupLoading } =
  useAddRemoveMembersFromGroup();
useOverlayLoading('isUpdateGroupLoading', isUpdateGroupLoading);

// 削除モーダルの表示制御用変数
const isOpenModal = ref(false);
const isError = ref(false);

// ユーザー削除ボタンでモーダル表示
const onClickDeleteModalOpen = () => {
  // 選択されている場合は確認モーダルを表示
  heading.value = messages.group.deleteMember;
  isOpenModal.value = true;
};

// 削除ボタン押下時の処理
const onClickRemove = async () => {
  // 削除対象のユーザーを絞り込み
  const groupRemoveUserIds = groupAssignedUsers.value
    .filter(user => user.isChecked && user.status === userStatus.active)
    .map(user => user.user.auth0UserId);

  // 削除対象の招待を絞り込み
  const groupRemoveInvitationEmails = groupAssignedUsers.value
    .filter(
      user =>
        user.isChecked &&
        (user.status === userStatus.invited ||
          user.status === userStatus.expired)
    )
    .map(user => user.user.email);

  const groupRemoveInput: AddOrRemoveGroupInput = {
    action: Action.remove,
    groupId: props.groupId,
  };

  if (groupRemoveUserIds.length > 0) {
    groupRemoveInput.activeUsers = { auth0UserIds: groupRemoveUserIds };
  }

  if (groupRemoveInvitationEmails.length > 0) {
    groupRemoveInput.invitedUsers = { emails: groupRemoveInvitationEmails };
  }

  await addOrRemoveMembersFromGroup(groupRemoveInput);
};

const onClickGroupAssign = async (auth0UserIds: string[]) => {
  isGroupAssignDialogOpen.value = false;
  if (auth0UserIds && auth0UserIds.length !== 0) {
    const groupAttachInput = {
      action: Action.add,
      groupId: props.groupId,
      activeUsers: { auth0UserIds: auth0UserIds },
    };
    await addOrRemoveMembersFromGroup(groupAttachInput);
  }
};

const addOrRemoveMembersFromGroup = async (input: AddOrRemoveGroupInput) => {
  const action = input.action;
  try {
    const res = await addRemoveMembersFromGroup({ input: input });
    if (res?.data?.addRemoveMembersFromGroup?.statusCode === Message.success) {
      isOpenModal.value = false;
      pushFeedback({
        type: 'success',
        heading:
          action === Action.add
            ? messages.group.groupManagement.success.assignGroup
            : messages.group.groupManagement.success.removeGroup,
      });
      usersNeeded.value = Users.both;
      filterOption.value = 'match';
      await listRefetch();
      emit('onUpdateGroup');
    } else {
      throw new Error('failed to delete');
    }
  } catch (e) {
    console.error(e);
    isOpenModal.value = true;
    isError.value = true;
    heading.value =
      action === Action.add
        ? messages.group.addMember
        : messages.group.deleteMember;
    errorText.value =
      action === Action.add ? groupAssignError : groupRemoveError;
  }
};

const onClickCancelButton = () => {
  isOpenModal.value = false;
  isError.value = false;
};
const onClickDeleteClose = () => {
  isOpenModal.value = false;
  isError.value = false;
};

const movePage = (pageNumber: number) => {
  currentPage.value = pageNumber;
};

watch(
  () => totalCount.value,
  () => {
    const totalPages = Math.ceil(totalCount.value / MAX_DISPLAY_NUM);

    // Adjust current page if needed (After deleting users)
    if (currentPage.value > totalPages) {
      currentPage.value = Math.max(totalPages, 1); // fallback to page 1 if all pages are gone
    }
  }
);

const start = computed(() => (currentPage.value - 1) * MAX_DISPLAY_NUM);
const end = computed(() =>
  Math.min(start.value + MAX_DISPLAY_NUM, totalCount.value)
);

const displayUsers = computed(() =>
  filteredUser.value.slice(start.value, end.value)
);

watchEffect(() => {
  const checkedDisplayUsersCount = checkedDisplayItemsLength();
  const checkboxDisplayItemsCount = checkboxDisplayItemsLength();
  if (
    checkedDisplayUsersCount === checkboxDisplayItemsCount &&
    checkedDisplayUsersCount !== 0
  ) {
    isThChecked.value = true;
  } else {
    isThChecked.value = false;
  }
});
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_pageForward.scss' as *;

.GroupManagement {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: $service-unit * 75;

  &__inner {
    margin: $service-unit * 5 0;
  }

  &__card {
    height: 100%;
    display: flex;
    padding: $service-unit * 4 $service-unit * 8 0;
    background-color: $color-gray-20;
  }

  & &__mainSection {
    padding: $service-unit * 4 $service-unit * 4 $service-unit * 6;
    display: flex;
    flex-direction: column;
    overflow: auto;
    background-color: $color-white-100;
    width: 100%;
  }

  &__inner,
  &__list {
    display: flex;
    align-items: center;
  }
  &__selectedSettings {
    margin-bottom: $service-unit * 7;
  }

  & &__searchForm {
    width: 100% * calc(400 / 1104);
    max-width: $service-unit * 100;
  }

  &__list {
    margin-left: auto;
  }

  &__listItem:not(:first-child) {
    margin-left: $service-unit * 6;
  }

  & &__button {
    min-height: $service-unit * 8;
    margin-bottom: $service-unit * 2;
  }

  & &__pagination {
    margin-top: $service-unit * 5;
  }

  &__modal {
    text-align: center;
  }

  &__modalText {
    font-size: $service-font-size-pp;
    line-height: $service-line-height-mf;
    text-align: center;
    margin-top: $service-unit * 2;
  }

  &__modalButton {
    display: flex;
    justify-content: center;
    margin-top: $service-unit * 4;
  }

  &__modalItem:not(:first-child) {
    margin-left: $service-unit * 3;
  }

  &__icon {
    width: $service-unit * 10;
    margin: $service-unit * 4 $service-unit * 38 0 $service-unit * 38;
    fill: #c33b2e;
  }

  &__information {
    display: block;
    font-size: $service-font-size-mp;
    margin: $service-unit * 3 0;
  }

  &__infoSkeleton {
    display: inline-block;
    vertical-align: middle;
    width: 15%;
    height: $service-unit * 5;
    background-color: $color-gray-20;
    border-radius: $service-border-radius * 2;
  }

  &__highlight {
    display: contents;
    white-space: noWrap;
    color: #ca52b0;
    font-weight: bold;
  }

  @media (max-width: 768px) {
    & &__searchForm {
      width: 45%;
    }
    &__infoSkeleton {
      width: 30%;
    }
  }

  @media (max-width: 424px) {
    &__icon {
      margin-left: $service-unit * 28;
    }
  }
}
</style>
